import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useLocation } from "react-router";
import { api } from "./api";
import { useSession } from "./auth";
import { config } from "./config";
import { useIsAdmin, useIsManager } from "./lib/permissions";
import { type RealtimeStatus, useRealtime } from "./realtime";
import type { AgentSummary, FactoryLayout, FactoryState, RealtimeEvent, Run, RunEvent, Task } from "./types";

export interface TickerItem {
  id: string;
  at: string;
  kind: "run" | "task" | "merge" | "notice";
  level: "info" | "success" | "warning" | "error";
  text: string;
  runId?: string;
  taskId?: string;
}

interface FactoryContextValue {
  layout: FactoryLayout | undefined;
  state: FactoryState | undefined;
  loading: boolean;
  error: Error | null;
  realtime: RealtimeStatus;
  ticker: TickerItem[];
  refresh: () => Promise<void>;
  agentById: Map<string, AgentSummary>;
  sectionOfAgent: Map<string, string>;
}

const FactoryContext = createContext<FactoryContextValue | undefined>(undefined);

export function FactoryProvider({ children }: { children: ReactNode }) {
  const session = useSession();
  const queryClient = useQueryClient();
  const [ticker, setTicker] = useState<TickerItem[]>([]);
  const seen = useRef(new Set<string>());

  const admin = useIsAdmin();
  const manager = useIsManager();
  const onTv = useLocation().pathname === "/tv";
  const watchFactory = admin || onTv;
  const signedIn = session.ready && session.authenticated;
  const enabled = signedIn && watchFactory;
  // Managers need station names on the issue page's Factory card, but not the live floor.
  const layoutQuery = useQuery({
    queryKey: ["layout"],
    queryFn: api.layout,
    enabled: signedIn && (watchFactory || manager),
    staleTime: 5 * 60_000,
  });
  const stateQuery = useQuery({
    queryKey: ["state"],
    queryFn: api.state,
    enabled,
    refetchInterval: config.pollIntervalMs,
  });

  const agentById = useMemo(() => {
    const map = new Map<string, AgentSummary>();
    for (const section of layoutQuery.data?.sections ?? [])
      for (const agent of section.agents) map.set(agent.id, agent);
    return map;
  }, [layoutQuery.data]);
  const sectionOfAgent = useMemo(() => {
    const map = new Map<string, string>();
    for (const section of layoutQuery.data?.sections ?? [])
      for (const agent of section.agents) map.set(agent.id, section.id);
    return map;
  }, [layoutQuery.data]);

  const pushTicker = useCallback((item: TickerItem) => {
    if (seen.current.has(item.id)) return;
    seen.current.add(item.id);
    setTicker((items) => [item, ...items].sort((a, b) => b.at.localeCompare(a.at)).slice(0, 60));
  }, []);

  const onEvent = useCallback(
    (event: RealtimeEvent) => {
      queryClient.setQueryData<FactoryState>(["state"], (current) =>
        current ? applyEvent(current, event) : current,
      );
      const item = tickerItem(event, agentById);
      if (item) pushTicker(item);
      if (event.type === "run.event") {
        queryClient.setQueryData<RunEvent[]>(["run-live", event.runId], (current = []) => {
          const row: RunEvent = {
            id: event.event.seq,
            runId: event.runId,
            seq: event.event.seq,
            type: event.event.type,
            payload: event.event.payload,
            createdAt: event.event.createdAt,
          };
          if (current.some((item) => item.seq === row.seq)) return current;
          return [...current, row];
        });
      }
      if (event.type === "run.updated") {
        queryClient.invalidateQueries({ queryKey: ["run-events", event.run.id] });
      }
      if (event.type === "task.updated" || event.type === "run.updated" || event.type === "merge.updated") {
        const taskId =
          event.type === "task.updated"
            ? event.task.id
            : event.type === "run.updated"
              ? event.run.taskId
              : event.item.taskId;
        queryClient.invalidateQueries({ queryKey: ["task", taskId] });
        queryClient.invalidateQueries({ queryKey: ["tasks"] });
      }
      if (event.type === "issue.updated" || event.type === "issue.comment") {
        queryClient.invalidateQueries({ queryKey: ["issues"] });
        queryClient.invalidateQueries({ queryKey: ["issue", event.identifier] });
        if (event.type === "issue.comment") {
          queryClient.invalidateQueries({ queryKey: ["issue-comments", event.identifier] });
        }
      }
      if (event.type === "preview.updated") {
        queryClient.invalidateQueries({ queryKey: ["previews"] });
        queryClient.invalidateQueries({ queryKey: ["preview", event.preview.id] });
        if (event.identifier) queryClient.invalidateQueries({ queryKey: ["issue", event.identifier] });
      }
      if (event.type === "release.updated") {
        queryClient.invalidateQueries({ queryKey: ["releases"] });
        queryClient.invalidateQueries({ queryKey: ["release", event.release.id] });
        queryClient.invalidateQueries({ queryKey: ["latest-release"] });
        // Issue pages key by identifier; the cheapest correct move is to refresh every open issue query.
        if (event.issueIds.length) queryClient.invalidateQueries({ queryKey: ["issue"] });
        queryClient.invalidateQueries({ queryKey: ["issues"] });
      }
    },
    [queryClient, agentById, pushTicker],
  );
  const realtime = useRealtime(session.token, watchFactory ? "factory" : "issues", onEvent);

  // Without realtime, derive ticker entries from state polling so the floor still feels alive.
  const previousRuns = useRef<Map<string, Run>>(new Map());
  useEffect(() => {
    const runs = stateQuery.data?.runs ?? [];
    for (const run of runs) {
      const before = previousRuns.current.get(run.id);
      if (!before || before.status !== run.status) {
        const item = tickerItem({ type: "run.updated", run }, agentById);
        if (item) pushTicker({ ...item, id: `${run.id}:${run.status}` });
      }
    }
    previousRuns.current = new Map(runs.map((r) => [r.id, r]));
  }, [stateQuery.data, agentById, pushTicker]);

  const value: FactoryContextValue = {
    layout: layoutQuery.data,
    state: stateQuery.data,
    loading: layoutQuery.isLoading || stateQuery.isLoading,
    error: (layoutQuery.error ?? stateQuery.error) as Error | null,
    realtime,
    ticker,
    refresh: async () => {
      await Promise.all([layoutQuery.refetch(), stateQuery.refetch()]);
    },
    agentById,
    sectionOfAgent,
  };
  return <FactoryContext.Provider value={value}>{children}</FactoryContext.Provider>;
}

export function useFactory(): FactoryContextValue {
  const value = useContext(FactoryContext);
  if (!value) throw new Error("useFactory must be used inside FactoryProvider");
  return value;
}

function applyEvent(state: FactoryState, event: RealtimeEvent): FactoryState {
  switch (event.type) {
    case "task.updated": {
      const previous = state.tasks.find((task) => task.id === event.task.id);
      const task: Task = {
        ...event.task,
        issueTypeKey:
          event.task.issueSnapshot?.type ?? event.task.issueTypeKey ?? previous?.issueTypeKey ?? null,
      };
      return { ...state, tasks: upsert(state.tasks, task) };
    }
    case "run.updated": {
      const runs = upsert(state.runs, event.run);
      return { ...state, runs, stations: recomputeStations(state, runs) };
    }
    case "merge.updated":
      return { ...state, mergeQueue: upsert(state.mergeQueue, event.item) };
    default:
      return state;
  }
}

function upsert<T extends { id: string }>(items: T[], item: T): T[] {
  const index = items.findIndex((i) => i.id === item.id);
  if (index === -1) return [item, ...items];
  const next = items.slice();
  next[index] = item;
  return next;
}

function recomputeStations(state: FactoryState, runs: Run[]): FactoryState["stations"] {
  const stations: FactoryState["stations"] = {};
  for (const [agentId, station] of Object.entries(state.stations)) {
    const mine = runs.filter((r) => r.agentId === agentId);
    stations[agentId] = {
      ...station,
      active: mine.filter((r) => ["launching", "running", "waiting_for_input"].includes(r.status)).length,
      queued: mine.filter((r) => r.status === "queued").length,
      blocked: mine.filter((r) => r.status === "blocked").length,
    };
  }
  return stations;
}

function tickerItem(event: RealtimeEvent, agents: Map<string, AgentSummary>): TickerItem | undefined {
  const at = new Date().toISOString();
  switch (event.type) {
    case "run.updated": {
      const agent = agents.get(event.run.agentId)?.name ?? event.run.agentId;
      const level =
        event.run.status === "failed" || event.run.status === "expired"
          ? "error"
          : event.run.status === "completed"
            ? "success"
            : event.run.status === "blocked"
              ? "warning"
              : "info";
      return {
        id: `${event.run.id}:${event.run.status}:${event.run.updatedAt}`,
        at: event.run.updatedAt,
        kind: "run",
        level,
        text: `${agent} · ${event.run.status.replace(/_/g, " ")}${event.run.verdict ? ` · ${event.run.verdict}` : ""}${event.run.error ? ` · ${event.run.error}` : ""}`,
        runId: event.run.id,
        taskId: event.run.taskId,
      };
    }
    case "run.event": {
      const { type, payload } = event.event;
      if (type === "assistant" && typeof payload.text === "string" && payload.text.trim()) {
        return {
          id: `${event.runId}:${event.event.seq}`,
          at,
          kind: "run",
          level: "info",
          text: payload.text.slice(0, 160),
          runId: event.runId,
          taskId: event.taskId,
        };
      }
      if (type === "tool_call" && typeof payload.name === "string") {
        return {
          id: `${event.runId}:${event.event.seq}`,
          at,
          kind: "run",
          level: "info",
          text: `tool ${payload.name} ${payload.status ?? ""}`,
          runId: event.runId,
          taskId: event.taskId,
        };
      }
      return undefined;
    }
    case "merge.updated":
      return {
        id: `${event.item.id}:${event.item.status}:${event.item.updatedAt}`,
        at,
        kind: "merge",
        level: event.item.status === "merged" ? "success" : event.item.status === "failed" ? "error" : "info",
        text: `Merge queue · PR #${event.item.prNumber} ${event.item.status}${event.item.releaseTag ? ` · ${event.item.releaseTag}` : ""}`,
        taskId: event.item.taskId,
      };
    case "task.updated":
      return undefined;
    case "release.updated": {
      const { release } = event;
      if (!["published", "deploying", "deployed", "failed"].includes(release.status)) return undefined;
      return {
        id: `${release.id}:${release.status}:${release.updatedAt}`,
        at: release.updatedAt,
        kind: "notice",
        level: release.status === "deployed" ? "success" : release.status === "failed" ? "error" : "info",
        text: `Release ${release.tag ?? release.name} · ${release.status}${release.error ? ` · ${release.error}` : ""}`,
      };
    }
    case "factory.notice":
      return {
        id: `${at}:${event.message}`,
        at,
        kind: "notice",
        level: event.level === "error" ? "error" : event.level === "warning" ? "warning" : "info",
        text: event.message,
      };
  }
}

export function taskOfRun(state: FactoryState | undefined, run: Run): Task | undefined {
  return state?.tasks.find((t) => t.id === run.taskId);
}
